// main.js
import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import store from './store';
import 'primeicons/primeicons.css'
import { updateService, initializeServices } from '@/services';

import PrimeVue from 'primevue/config';
import AutoComplete from 'primevue/autocomplete';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import Avatar from 'primevue/avatar';
import AvatarGroup from 'primevue/avatargroup';
import Badge from 'primevue/badge';
import BadgeDirective from 'primevue/badgedirective';
import BlockUI from 'primevue/blockui';
import Button from 'primevue/button';
import ButtonGroup from 'primevue/buttongroup';
import Breadcrumb from 'primevue/breadcrumb';
import Calendar from 'primevue/calendar';
import Card from 'primevue/card';
import Chart from 'primevue/chart';
import CascadeSelect from 'primevue/cascadeselect';
import Carousel from 'primevue/carousel';
import Checkbox from 'primevue/checkbox';
import Chip from 'primevue/chip';
import Chips from 'primevue/chips';
import ColorPicker from 'primevue/colorpicker';
import Column from 'primevue/column';
import ColumnGroup from 'primevue/columngroup';
import ConfirmDialog from 'primevue/confirmdialog';
import ConfirmPopup from 'primevue/confirmpopup';
import ConfirmationService from 'primevue/confirmationservice';
import ContextMenu from 'primevue/contextmenu';
import DataTable from 'primevue/datatable';
import DataView from 'primevue/dataview';
import DataViewLayoutOptions from 'primevue/dataviewlayoutoptions';
import DeferredContent from 'primevue/deferredcontent';
import Dialog from 'primevue/dialog';
import DialogService from 'primevue/dialogservice';
import Divider from 'primevue/divider';
import Dock from 'primevue/dock';
import Dropdown from 'primevue/dropdown';
import DynamicDialog from 'primevue/dynamicdialog';
import Editor from 'primevue/editor';
import Fieldset from 'primevue/fieldset';
import FileUpload from 'primevue/fileupload';
import FloatLabel from 'primevue/floatlabel';
import Galleria from 'primevue/galleria';
import IconField from 'primevue/iconfield';
import Image from 'primevue/image';
import InlineMessage from 'primevue/inlinemessage';
import Inplace from 'primevue/inplace';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputIcon from 'primevue/inputicon';
import InputSwitch from 'primevue/inputswitch';
import InputText from 'primevue/inputtext';
import InputMask from 'primevue/inputmask';
import InputNumber from 'primevue/inputnumber';
import Knob from 'primevue/knob';
import Listbox from 'primevue/listbox';
import MegaMenu from 'primevue/megamenu';
import Menu from 'primevue/menu';
import Menubar from 'primevue/menubar';
import Message from 'primevue/message';
import MultiSelect from 'primevue/multiselect';
import OrderList from 'primevue/orderlist';
import OrganizationChart from 'primevue/organizationchart';
import OverlayPanel from 'primevue/overlaypanel';
import Paginator from 'primevue/paginator';
import Panel from 'primevue/panel';
import PanelMenu from 'primevue/panelmenu';
import Password from 'primevue/password';
import PickList from 'primevue/picklist';
import ProgressBar from 'primevue/progressbar';
import ProgressSpinner from 'primevue/progressspinner';
import Rating from 'primevue/rating';
import RadioButton from 'primevue/radiobutton';
import Ripple from 'primevue/ripple';
import Row from 'primevue/row';
import SelectButton from 'primevue/selectbutton';
import ScrollPanel from 'primevue/scrollpanel';
import ScrollTop from 'primevue/scrolltop';
import Skeleton from 'primevue/skeleton';
import Slider from 'primevue/slider';
import Sidebar from 'primevue/sidebar';
import SpeedDial from 'primevue/speeddial';
import SplitButton from 'primevue/splitbutton';
import Splitter from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';
import Steps from 'primevue/steps';
import StyleClass from 'primevue/styleclass';
import TabMenu from 'primevue/tabmenu';
import TieredMenu from 'primevue/tieredmenu';
import Textarea from 'primevue/textarea';
import Toast from 'primevue/toast';
import ToastService from 'primevue/toastservice';
import Toolbar from 'primevue/toolbar';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Tag from 'primevue/tag';
import Terminal from 'primevue/terminal';
import Timeline from 'primevue/timeline';
import ToggleButton from 'primevue/togglebutton';
import Tooltip from 'primevue/tooltip';
import Tree from 'primevue/tree';
import TreeSelect from 'primevue/treeselect';
import TreeTable from 'primevue/treetable';
import TriStateCheckbox from 'primevue/tristatecheckbox';
import VirtualScroller from 'primevue/virtualscroller';

import BlockViewer from '@/components/BlockViewer.vue';

import vue3GoogleLogin from 'vue3-google-login'

import '@/assets/styles.scss';

import localforage from 'localforage';

localforage.config({
  driver: [
    localforage.INDEXEDDB,
    localforage.WEBSQL,
    localforage.LOCALSTORAGE
  ],
  name: 'peppr-app',
  version: 1.0,
  storeName: 'peppr_store'
});

const app = createApp(App);

router.afterEach((to) => {
  // Envoie l'événement de page vue à chaque changement de route
  if (window.gtag) {
    window.gtag('config', 'G-07WV4QD1R0', {
      page_path: to.fullPath,
      page_title: to.name,
    });
  }
  
  // Ne pas réinitialiser le service de logs à chaque changement de route
  // Enregistrer le log localement sans envoi immédiat au serveur
  store.dispatch('logs/info', { 
    message: `Navigation vers ${to.name}`,
    data: { 
      path: to.fullPath,
      params: to.params,
      query: to.query
    },
    sendImmediately: false // Désactiver l'envoi immédiat pour les logs de navigation
  });
});

Promise.all([
  store.dispatch('restoreAuth'),
  localforage.ready(),
  initializeServices(), // Initialiser les services au démarrage
]).then(() => {

  app.use(router);
  app.use(store);
  app.use(ToastService);
  app.use(DialogService);
  app.use(ConfirmationService);

  // Écouteurs pour gérer les problèmes de connexion à IndexedDB
  if (typeof window !== 'undefined') {
    // Événement quand le stockage passe en mode dégradé
    window.addEventListener('storage-degraded', (event) => {
      const toast = app.config.globalProperties.$toast;
      if (toast) {
        toast.add({
          severity: 'warn',
          summary: 'Problème de stockage local',
          detail: event.detail?.message || 'Problème avec la base de données locale. Les fonctionnalités hors ligne peuvent être limitées.',
          life: 10000,
          closable: true
        });
      }
      
      // Logger l'erreur
      store.dispatch('logs/error', {
        message: 'Passage en mode de stockage dégradé',
        data: {
          reason: event.detail?.reason,
          timestamp: event.detail?.timestamp
        }
      });
    });
    
    // Événement quand le stockage est restauré
    window.addEventListener('storage-restored', () => {
      // Toast supprimé pour ne plus afficher la notification
      
      // Logger l'info (conservé)
      store.dispatch('logs/info', {
        message: 'Stockage local restauré',
        data: {
          timestamp: new Date().toISOString()
        }
      });
    });
  }

  // Initialiser le service de logs
  store.dispatch('logs/initLogService', {
    // Configuration spécifique pour l'application
    apiEndpoint: '/livraison/logs/appDelivery/',
    minLevel: process.env.NODE_ENV === 'production' ? 1 : 0, // INFO en prod, DEBUG en dev
    minServerLevel: process.env.NODE_ENV === 'production' ? 2 : 1, // WARN en prod, INFO en dev
    priorityCategories: ['sync_data', 'error'],  // Toujours envoyer les logs de synchronisation et d'erreur
    consoleOutput: process.env.NODE_ENV !== 'production',
    overrideConsole: false, // Ne pas surcharger console.log en prod
    captureUnhandledErrors: true
  });

  app.use(PrimeVue, {
    ripple: true,
    locale: {
      firstDayOfWeek: 1,
      dayNames: ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"],
      dayNamesShort: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
      dayNamesMin: ["Di", "Lu", "Ma", "Me", "Je", "Ve", "Sa"],
      monthNames: ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"],
      monthNamesShort: ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"],
      today: 'Aujourd\'hui',
      clear: 'Effacer',
      weekHeader: 'Sem'
    },
    // Ajoutez cette fonction pour désactiver les weekends
    dateOptions: {
      disabledDays: [0, 6], // 0 pour dimanche, 6 pour samedi
      disabledDates: null
    },
    inputStyle: 'filled',
    unstyled: false,
    pt: {
      button: {
        root: {
          class: 'offline-button-class'
        }
      },
      icon: {
        root: {
          class: 'integrated-icon'
        }
      }
    }
  });

  app.use(vue3GoogleLogin, {
    clientId: '655441094356-vmhp4p111of9ifofgd6q39612kt7h9c9.apps.googleusercontent.com'
  });

  // Initialisation de Google Analytics si le consentement est donné
  const ga4Enabled = JSON.parse(localStorage.getItem('ga4_enabled') || 'false');
  
  // Créer la fonction gtag qui ne fait rien si Google Analytics est désactivé
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function() {
    window.dataLayer.push(arguments);
  };
  
  if (ga4Enabled) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=G-07WV4QD1R0`;
    document.head.appendChild(script);

    window.gtag('js', new Date());
    window.gtag('config', 'G-07WV4QD1R0');
  }

  app.directive('tooltip', Tooltip);
  app.directive('badge', BadgeDirective);
  app.directive('ripple', Ripple);
  app.directive('styleclass', StyleClass);

  app.component('BlockViewer', BlockViewer);

  app.component('Accordion', Accordion);
  app.component('AccordionTab', AccordionTab);
  app.component('AutoComplete', AutoComplete);
  app.component('Avatar', Avatar);
  app.component('AvatarGroup', AvatarGroup);
  app.component('Badge', Badge);
  app.component('BlockUI', BlockUI);
  app.component('Breadcrumb', Breadcrumb);
  app.component('Button', Button);
  app.component('ButtonGroup', ButtonGroup);
  app.component('Calendar', Calendar);
  app.component('Card', Card);
  app.component('Chart', Chart);
  app.component('Carousel', Carousel);
  app.component('CascadeSelect', CascadeSelect);
  app.component('Checkbox', Checkbox);
  app.component('Chip', Chip);
  app.component('Chips', Chips);
  app.component('ColorPicker', ColorPicker);
  app.component('Column', Column);
  app.component('ColumnGroup', ColumnGroup);
  app.component('ConfirmDialog', ConfirmDialog);
  app.component('ConfirmPopup', ConfirmPopup);
  app.component('ContextMenu', ContextMenu);
  app.component('DataTable', DataTable);
  app.component('DataView', DataView);
  app.component('DataViewLayoutOptions', DataViewLayoutOptions);
  app.component('DeferredContent', DeferredContent);
  app.component('Dialog', Dialog);
  app.component('Divider', Divider);
  app.component('Dock', Dock);
  app.component('Dropdown', Dropdown);
  app.component('DynamicDialog', DynamicDialog);
  app.component('Editor', Editor);
  app.component('Fieldset', Fieldset);
  app.component('FileUpload', FileUpload);
  app.component('FloatLabel', FloatLabel);
  app.component('Galleria', Galleria);
  app.component('IconField', IconField);
  app.component('Image', Image);
  app.component('InlineMessage', InlineMessage);
  app.component('Inplace', Inplace);
  app.component('InputGroup', InputGroup);
  app.component('InputGroupAddon', InputGroupAddon);
  app.component('InputIcon', InputIcon);
  app.component('InputMask', InputMask);
  app.component('InputNumber', InputNumber);
  app.component('InputSwitch', InputSwitch);
  app.component('InputText', InputText);
  app.component('Knob', Knob);
  app.component('Listbox', Listbox);
  app.component('MegaMenu', MegaMenu);
  app.component('Menu', Menu);
  app.component('Menubar', Menubar);
  app.component('Message', Message);
  app.component('MultiSelect', MultiSelect);
  app.component('OrderList', OrderList);
  app.component('OrganizationChart', OrganizationChart);
  app.component('OverlayPanel', OverlayPanel);
  app.component('Paginator', Paginator);
  app.component('Panel', Panel);
  app.component('PanelMenu', PanelMenu);
  app.component('Password', Password);
  app.component('PickList', PickList);
  app.component('ProgressBar', ProgressBar);
  app.component('ProgressSpinner', ProgressSpinner);
  app.component('RadioButton', RadioButton);
  app.component('Rating', Rating);
  app.component('Row', Row);
  app.component('SelectButton', SelectButton);
  app.component('ScrollPanel', ScrollPanel);
  app.component('ScrollTop', ScrollTop);
  app.component('Slider', Slider);
  app.component('Sidebar', Sidebar);
  app.component('Skeleton', Skeleton);
  app.component('SpeedDial', SpeedDial);
  app.component('SplitButton', SplitButton);
  app.component('Splitter', Splitter);
  app.component('SplitterPanel', SplitterPanel);
  app.component('Steps', Steps);
  app.component('TabMenu', TabMenu);
  app.component('TabView', TabView);
  app.component('TabPanel', TabPanel);
  app.component('Tag', Tag);
  app.component('Textarea', Textarea);
  app.component('Terminal', Terminal);
  app.component('TieredMenu', TieredMenu);
  app.component('Timeline', Timeline);
  app.component('Toast', Toast);
  app.component('Toolbar', Toolbar);
  app.component('ToggleButton', ToggleButton);
  app.component('Tree', Tree);
  app.component('TreeSelect', TreeSelect);
  app.component('TreeTable', TreeTable);
  app.component('TriStateCheckbox', TriStateCheckbox);
  app.component('VirtualScroller', VirtualScroller);

  app.mount('#app');
  window.app = app; // Exposer l'instance app globalement pour l'eventBus
  store.dispatch('sync/initializeNetworkListeners');
  // La synchronisation automatique sera gérée par l'initialisation des listeners réseau
  // si nous sommes en ligne, donc nous n'avons pas besoin de l'appeler directement ici
  if (navigator.onLine) {
    console.log("L'application est en ligne, la synchronisation sera gérée automatiquement.");
    // Nous ne forçons plus la synchronisation ici car elle sera déjà démarrée par les listeners réseau
    // store.dispatch('sync/synchronize');
    
    // Synchroniser les logs au démarrage
    store.dispatch('logs/synchronizeLogs');
  };

  // Initialiser le service de mise à jour avec le service Toast
  updateService.init(app.config.globalProperties.$toast);
  
  // Activer les mises à jour automatiques toutes les 15 minutes si l'application est en ligne
  // Cette fonctionnalité permet de garder l'application à jour sans intervention de l'utilisateur
  if (navigator.onLine) {
    updateService.setAutoUpdateEnabled(true);
    console.log('Mises à jour automatiques activées (intervalle : 15 minutes)');
  }

  // Optional: Show initial connection status
  const toast = app.config.globalProperties.$toast;
  if (!navigator.onLine) {
    toast.add({
      severity: 'info',
      summary: 'Mode hors ligne',
      detail: 'L\'application fonctionne en mode hors ligne',
      life: 3000
    });
    // Logger l'état hors ligne
    store.dispatch('logs/warn', { 
      message: 'Application démarrée en mode hors ligne',
      data: { startTime: new Date().toISOString() },
      sendImmediately: false // Désactiver l'envoi immédiat
    });
  } else {
    // Logger le démarrage en ligne
    store.dispatch('logs/info', { 
      message: 'Application démarrée en mode en ligne',
      data: { startTime: new Date().toISOString() },
      sendImmediately: false // Désactiver l'envoi immédiat
    });
  }

  
  // Enregistrement du service worker
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        // Choisir le bon fichier Service Worker en fonction du mode
        const swFile = process.env.NODE_ENV === 'development' ? '/sw-dev.js' : '/sw.js';
        console.log(`Enregistrement du Service Worker: ${swFile} (mode: ${process.env.NODE_ENV})`);
        
        // Vérifier d'abord si un service worker est déjà enregistré
        const existingRegistration = await navigator.serviceWorker.getRegistration();
        if (existingRegistration) {
          console.log('Service Worker déjà enregistré, mise à jour...');
          await existingRegistration.update();
          console.log('Service Worker mis à jour avec succès');
          
          // Configurer le service de mise à jour pour écouter les changements du service worker
          updateService.setupUpdateListeners(existingRegistration);
          
          // Utiliser l'enregistrement existant pour les événements
          handleServiceWorkerEvents(existingRegistration);
          return;
        }
        
        // Vérifier que les fichiers existent avant l'enregistrement
        const swResponse = await fetch(swFile);
        if (!swResponse.ok) {
          throw new Error(`Le fichier ${swFile} n'a pas pu être trouvé (status: ${swResponse.status})`);
        }
        
        // Tenter l'enregistrement avec des options explicites
        const registration = await navigator.serviceWorker.register(swFile, {
          scope: '/',
          updateViaCache: 'none' // Ne pas utiliser le cache pour les mises à jour
        });
        
        console.log('Service Worker enregistré avec succès:', registration.scope);
        
        // Stocker la date de dernière synchronisation pour l'afficher en mode hors ligne
        if (store.state.sync && store.state.sync.lastSuccessfulSync) {
          localStorage.setItem('lastSuccessfulSync', store.state.sync.lastSuccessfulSync);
        }
        
        // Configurer le service de mise à jour pour écouter les changements du service worker
        updateService.setupUpdateListeners(registration);
        
        // Gérer les événements du service worker
        handleServiceWorkerEvents(registration);
        
      } catch (error) {
        console.error('Échec de l\'enregistrement du service worker:', error);
        
        // Informer l'utilisateur de l'échec en mode développement
        if (process.env.NODE_ENV !== 'production') {
          const toast = app.config.globalProperties.$toast;
          toast.add({
            severity: 'warn',
            summary: 'Service Worker',
            detail: 'Impossible d\'activer le mode hors ligne: ' + error.message,
            life: 5000
          });
        }
        
        // Récupérer la version du serveur de développement pour plus d'informations
        try {
          const response = await fetch('/version');
          console.log('Version du serveur de développement:', await response.text());
        } catch (e) {
          console.log('Impossible de récupérer la version du serveur:', e);
        }
      }
    });
    
    // Écouter les messages du service worker
    navigator.serviceWorker.addEventListener('message', event => {
      console.log('Message reçu du service worker:', event.data);
      
      // Traiter les messages spécifiques comme nécessaire
      if (event.data.type === 'CACHE_UPDATED') {
        console.log('Cache mis à jour:', event.data.url);
      } else if (event.data.type === 'UPDATE_APPLIED') {
        console.log('Mise à jour appliquée, rechargement nécessaire');
        // Recharger la page automatiquement si une mise à jour a été appliquée
        window.location.reload();
      }
    });
    
    // Fonctions d'utilitaire pour les messages au service worker
    window.swUtils = {
      update: () => {
        navigator.serviceWorker.ready.then(registration => {
          registration.update();
        });
      },
      
      skipWaiting: () => {
        navigator.serviceWorker.controller?.postMessage({
          type: 'SKIP_WAITING'
        });
      },
      
      // Nouvelle fonction pour mettre en cache une route spécifique
      cacheRoute: (route) => {
        navigator.serviceWorker.controller?.postMessage({
          type: 'CACHE_ROUTE',
          route: route
        });
      },
      
      // Nouvelle fonction pour indiquer qu'une synchronisation a été effectuée
      notifySyncComplete: (timestamp) => {
        navigator.serviceWorker.controller?.postMessage({
          type: 'SYNC_TIMESTAMP',
          timestamp: timestamp || Date.now()
        });
      },
      
      // Nouvelle fonction pour vérifier la version du service worker
      checkVersion: () => {
        return new Promise((resolve) => {
          if (!navigator.serviceWorker.controller) {
            resolve(null);
            return;
          }
          
          const messageChannel = new MessageChannel();
          messageChannel.port1.onmessage = (event) => {
            resolve(event.data.version);
          };
          
          navigator.serviceWorker.controller.postMessage(
            { type: 'GET_VERSION' },
            [messageChannel.port2]
          );
          
          // Timeout après 1 seconde
          setTimeout(() => resolve(null), 1000);
        });
      }
    };
  } else {
    console.warn('Les Service Workers ne sont pas pris en charge par ce navigateur.');
  }
  
  // Fonction pour gérer les événements du service worker
  function handleServiceWorkerEvents(registration) {
    // Gérer les mises à jour du service worker
    registration.onupdatefound = () => {
      const installingWorker = registration.installing;
      
      if (installingWorker) {
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              console.log('Nouvelle version disponible. Utiliser updateService pour gérer la mise à jour.');
              // La gestion de la mise à jour est maintenant déléguée au service de mise à jour
            } else {
              console.log('Application mise en cache pour une utilisation hors ligne.');
              
              // Informer l'utilisateur que l'application est prête pour une utilisation hors ligne
              const toast = app.config.globalProperties.$toast;
              toast.add({
                severity: 'success',
                summary: 'Prêt pour le mode hors ligne',
                detail: 'L\'application est maintenant disponible même sans connexion internet',
                life: 3000
              });
            }
          }
        };
      }
    };
    
    // Mettre à jour le service worker lorsque l'utilisateur se reconnecte
    window.addEventListener('online', () => {
      registration.update();
    });
  }
}).catch(error => {
  console.error('Failed to initialize app:', error);
});